import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import { format } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faCheck, faUsers, faSearch } from '@fortawesome/free-solid-svg-icons';

const AttendanceBySupervisor = () => {
  const authState = useSelector(state => state.authReducer);
  const userId = authState.user._id;
  const userCode = authState.user.think5whyscode;
  const userName = authState.user.name;

  const [fetchData, { loading }] = useFetch();
  const [attendanceData, setAttendanceData] = useState([]);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [sortField, setSortField] = useState('attendanceDate');
  const [sortOrder, setSortOrder] = useState('desc');

  const [expandedDates, setExpandedDates] = useState(new Set());
  const [expandedEmployees, setExpandedEmployees] = useState(new Set());
  const [selectedRecordForApproval, setSelectedRecordForApproval] = useState(null);
  const [approverRemarks, setApproverRemarks] = useState('');

  const fetchAttendanceList = useCallback(() => {
    const config = {
      url: "/attendance/supervisorcode",
      method: "post",
      headers: { Authorization: authState.token },
      data: { userId, supervisorCode: userCode }
    };

    fetchData(config, { showSuccessToast: false })
      .then(response => {
        if (response?.attendanceList) setAttendanceData(response.attendanceList);
      })
      .catch(error => console.error(error));
  }, [authState.token, fetchData, userId, userCode]);

  useEffect(() => {
    if (authState.isLoggedIn) fetchAttendanceList();
  }, [authState.isLoggedIn, fetchAttendanceList]);

  // Filtered & Sorted Data
  const filteredAndSortedData = useMemo(() => {
    let data = [...attendanceData];

    // Search filter (employee name or purpose)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      data = data.filter(item =>
        item.empName?.toLowerCase().includes(term) ||
        item.attendanceFor?.toLowerCase().includes(term) ||
        item.remarks?.toLowerCase().includes(term)
      );
    }

    // Date filter
    if (selectedDate) {
      data = data.filter(item => 
        format(new Date(item.attendanceDate), 'yyyy-MM-dd') === selectedDate
      );
    }

    // Sorting
    data.sort((a, b) => {
      let valA = a[sortField];
      let valB = b[sortField];

      if (sortField === 'attendanceDate' || sortField.includes('Time')) {
        valA = new Date(valA);
        valB = new Date(valB);
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return data;
  }, [attendanceData, searchTerm, selectedDate, sortField, sortOrder]);

  // Group filtered data
  const groupedAttendanceData = useMemo(() => {
    return filteredAndSortedData.reduce((acc, item) => {
      const dateKey = format(new Date(item.attendanceDate), 'yyyy-MM-dd');
      if (!acc[dateKey]) acc[dateKey] = {};
      const empKey = item.empName || 'Unknown';
      if (!acc[dateKey][empKey]) {
        acc[dateKey][empKey] = { empName: empKey, items: [] };
      }
      acc[dateKey][empKey].items.push(item);
      return acc;
    }, {});
  }, [filteredAndSortedData]);

  const toggleExpandDate = (date) => {
    setExpandedDates(prev => {
      const newSet = new Set(prev);
      newSet.has(date) ? newSet.delete(date) : newSet.add(date);
      return newSet;
    });
  };

  const toggleExpandEmployee = (empName) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      newSet.has(empName) ? newSet.delete(empName) : newSet.add(empName);
      return newSet;
    });
  };

  const approveNow = (recordId) => {
    const config = {
      url: `/attendance/approve`,
      method: "post",
      headers: { Authorization: authState.token },
      data: { attendanceId: recordId, userId, userCode, userName, approverRemarks, approvedDate: new Date() }
    };

    fetchData(config).then(() => {
      setSelectedRecordForApproval(null);
      setApproverRemarks('');
      fetchAttendanceList();
    });
  };

  const getTotalTime = (endTime, startTime) => {
    if (!endTime || !startTime) return 'N/A';
    const diffMs = new Date(endTime) - new Date(startTime);
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-gray-100">
      <header className="sticky top-0 z-50 bg-zinc-900 border-b border-yellow-500/30 px-4 py-5">
        <h1 className="text-2xl font-bold text-yellow-200 text-center flex items-center justify-center gap-3">
          <FontAwesomeIcon icon={faUsers} /> My Team's Attendance
        </h1>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="bg-zinc-900 rounded-3xl p-5 mb-8 border border-zinc-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-4 text-gray-500" />
              <input
                type="text"
                placeholder="Search employee or purpose..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-2xl pl-11 py-1 focus:border-yellow-400"
              />
            </div>

            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-1"
            />

            <div className="flex gap-2">
              <select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                className="bg-zinc-800 border border-zinc-700 rounded-2xl px-4 py-1 flex-1"
              >
                <option value="attendanceDate">Date</option>
                <option value="empName">Employee Name</option>
                <option value="attendanceFor">Purpose</option>
              </select>

              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="bg-zinc-800 border border-zinc-700 rounded-2xl px-2 py-1"
              >
                {sortOrder === 'asc' ? '↑' : '↓'}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {loading ? <Loader /> : Object.keys(groupedAttendanceData).length === 0 ? (
          <div className="text-center py-20 text-yellow-300 text-xl">
            No matching records found
          </div>
        ) : (
          Object.entries(groupedAttendanceData).map(([date, employees]) => (
            <div key={date} className="bg-zinc-900 border border-zinc-700 rounded-3xl mb-6 overflow-hidden">
              {/* Date Header */}
              <div 
                className="flex justify-between items-center px-6 py-5 cursor-pointer hover:bg-zinc-800"
                onClick={() => toggleExpandDate(date)}
              >
                <span className="font-semibold text-lg">{format(new Date(date), 'dd MMMM yyyy')}</span>
                <FontAwesomeIcon icon={expandedDates.has(date) ? faChevronUp : faChevronDown} className="text-yellow-400" />
              </div>

              {expandedDates.has(date) && (
                <div className="px-6 pb-6 space-y-6">
                  {Object.entries(employees).map(([empName, employeeData]) => (
                    <div key={empName} className="bg-zinc-800 rounded-2xl overflow-hidden">
                      <div 
                        className="px-5 py-4 bg-zinc-700 flex justify-between items-center cursor-pointer"
                        onClick={() => toggleExpandEmployee(empName)}
                      >
                        <span className="font-medium">{empName}</span>
                        <FontAwesomeIcon icon={expandedEmployees.has(empName) ? faChevronUp : faChevronDown} />
                      </div>

                      {expandedEmployees.has(empName) && (
                        <div className="p-5 space-y-4">
                          {employeeData.items.map(item => (
                            <div key={item._id} className="bg-zinc-900 p-5 rounded-2xl border border-zinc-700">
                              <div className="flex justify-between">
                                <span className="text-emerald-400 font-medium">{item.attendanceFor}</span>
                                <span className="text-gray-400">In: {format(new Date(item.startTime), 'HH:mm')}</span>
                              </div>

                              {item.endTime && (
                                <div className="text-yellow-300 mt-2">
                                  Total: {getTotalTime(item.endTime, item.startTime)}
                                </div>
                              )}

                              {item.remarks && <p className="text-gray-400 mt-3">{item.remarks}</p>}

                              {!item.isApproved && (
                                <button
                                  onClick={() => setSelectedRecordForApproval(item._id)}
                                  className="w-fit px-2 py-1 mt-4 text-emerald-400 hover:text-emerald-300 flex items-center gap-2"
                                >
                                  <FontAwesomeIcon icon={faCheck} /> Approve
                                </button>
                              )}

                              {/* Approval Form */}
                              {selectedRecordForApproval === item._id && (
                                <div className="mt-6 p-5 bg-zinc-800 rounded-2xl">
                                  <textarea
                                    value={approverRemarks}
                                    onChange={e => setApproverRemarks(e.target.value)}
                                    placeholder="Approval remarks..."
                                    className="w-full bg-zinc-900 border border-zinc-700 rounded-2xl p-4"
                                    style={{height: "100px"}}
                                  />
                                  <div className="flex gap-3 mt-4">
                                    <button onClick={() => approveNow(item._id)} className="flex-1 bg-emerald-600 w-fit px-2 py-1 rounded-2xl">
                                      Confirm Approval
                                    </button>
                                    <button onClick={() => setSelectedRecordForApproval(null)} className="flex-1 border border-zinc-600 w-fit px-2 py-1 rounded-2xl">
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AttendanceBySupervisor;